import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";

const API_BASE_URL = "https://api.itrustsecure.com";
function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function InvoicesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [copiedId, setCopiedId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/invoices/my`, { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setInvoices(Array.isArray(data) ? data : []);
    } catch (err) { console.warn(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleCreate() {
    if (!title.trim()) { window.alert("Title is required"); return; }
    if (!amount || Number(amount) <= 0) { window.alert("Amount must be greater than 0"); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE_URL}/invoices`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ title: title.trim(), description: description.trim() || null, amount: Number(amount) })
      });
      const data = await res.json();
      if (res.ok) { setShowCreate(false); setTitle(""); setDescription(""); setAmount(""); load(); }
      else { window.alert(data.error || "Could not create invoice"); }
    } catch { window.alert("Something went wrong"); }
    finally { setCreating(false); }
  }

  async function handleCancel(id) {
    if (!window.confirm("Cancel this invoice?")) return;
    setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, status: "CANCELED" } : inv));
    try {
      const res = await fetch(`${API_BASE_URL}/invoices/${id}`, { method: "DELETE", headers: authHeaders() });
      if (!res.ok) { const d = await res.json(); window.alert(d.error || "Could not cancel"); load(); }
    } catch { window.alert("Something went wrong"); load(); }
  }

  function copyLink(slug) {
    navigator.clipboard.writeText(`https://www.itrustsecure.com/invoice/${slug}`).then(() => {
      setCopiedId(slug);
      setTimeout(() => setCopiedId(null), 2000);
    });
  }

  const platformFee = amount ? (Number(amount) * 0.025).toFixed(2) : "0.00";
  const total = amount ? (Number(amount) * 1.025).toFixed(2) : "0.00";

  const s = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "transparent", color: "#0f172a", minHeight: "100vh" },
    main: { maxWidth: 800, margin: "0 auto", padding: "32px 20px" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 },
    pageTitle: { fontSize: 28, fontWeight: 800, color: "#0f172a" },
    createBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "12px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 14, boxShadow: "0 4px 12px rgba(59,130,246,0.25)" },
    card: { background: "rgba(255,255,255,0.75)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 16, padding: 20, marginBottom: 12, backdropFilter: "blur(8px)", boxShadow: "0 2px 12px rgba(59,130,246,0.08)" },
    invoiceTitle: { fontSize: 18, fontWeight: 700, marginBottom: 4, color: "#1e293b" },
    invoiceDesc: { fontSize: 13, color: "#64748b", marginBottom: 12 },
    invoiceAmount: { fontSize: 28, fontWeight: 800, color: "#1e40af", marginBottom: 12 },
    badge: (color) => ({ padding: "4px 10px", borderRadius: 999, background: `${color}15`, color, border: `1px solid ${color}30`, fontSize: 11, fontWeight: 700 }),
    actionRow: { display: "flex", gap: 10, flexWrap: "wrap" },
    copyBtn: { background: "rgba(59,130,246,0.08)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.25)", padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    cancelBtn: { background: "rgba(220,38,38,0.08)", color: "#dc2626", border: "1px solid rgba(220,38,38,0.25)", padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    viewBtn: { background: "rgba(124,58,237,0.08)", color: "#7c3aed", border: "1px solid rgba(124,58,237,0.25)", padding: "8px 16px", borderRadius: 10, cursor: "pointer", fontWeight: 700, fontSize: 13 },
    empty: { textAlign: "center", padding: "60px 20px", color: "#94a3b8" },
    overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, backdropFilter: "blur(4px)" },
    modal: { background: "#fff", border: "1px solid rgba(59,130,246,0.15)", borderRadius: 20, padding: 32, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(59,130,246,0.15)" },
    modalTitle: { fontSize: 22, fontWeight: 800, marginBottom: 6, color: "#0f172a" },
    modalSub: { fontSize: 13, color: "#64748b", marginBottom: 24 },
    label: { fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6, display: "block" },
    input: { width: "100%", padding: "12px 14px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    textarea: { width: "100%", padding: "12px 14px", background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none", minHeight: 80, resize: "vertical" },
    summaryBox: { background: "rgba(59,130,246,0.04)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 10, padding: 14, marginBottom: 16 },
    summaryRow: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#64748b", marginBottom: 6 },
    summaryTotal: { display: "flex", justifyContent: "space-between", fontSize: 17, fontWeight: 800, paddingTop: 10, borderTop: "1px solid rgba(59,130,246,0.1)", color: "#0f172a" },
    modalActions: { display: "flex", gap: 10 },
    modalCancel: { flex: 1, background: "transparent", border: "1px solid rgba(59,130,246,0.2)", color: "#64748b", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700 },
    modalSubmit: { flex: 2, background: "#3b82f6", color: "#fff", border: "none", padding: "12px", borderRadius: 10, cursor: "pointer", fontWeight: 700 },
    paidBadge: { display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", borderRadius: 10, padding: "8px 14px", fontSize: 13, color: "#16a34a", fontWeight: 700, marginTop: 8 },
  };

  return (
    <div style={s.root}>
      <NavBar user={user} onLogout={onLogout} />
      <div style={s.main}>
        <div style={s.header}>
          <div>
            <div style={s.pageTitle}>🧾 Invoices</div>
            <div style={{ fontSize: 14, color: "#64748b", marginTop: 4 }}>Create secure payment links to share with anyone</div>
          </div>
          <button style={s.createBtn} onClick={() => setShowCreate(true)}>+ New Invoice</button>
        </div>

        {loading ? (
          <div style={s.empty}>Loading...</div>
        ) : invoices.length === 0 ? (
          <div style={s.empty}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🧾</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8, color: "#475569" }}>No invoices yet</div>
            <div style={{ marginBottom: 20 }}>Create a secure payment link and share it with anyone</div>
            <button style={s.createBtn} onClick={() => setShowCreate(true)}>Create First Invoice</button>
          </div>
        ) : invoices.map(inv => {
          const isPaid = inv.status === "PAID";
          const isCanceled = inv.status === "CANCELED";
          const payment = inv.payments?.[0];
          const statusColor = isPaid ? "#16a34a" : isCanceled ? "#6b7280" : "#d97706";
          const statusLabel = isPaid ? "✅ Paid" : isCanceled ? "Canceled" : "⏳ Pending";

          return (
            <div key={inv.id} style={s.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={s.invoiceTitle}>{inv.title}</div>
                <span style={s.badge(statusColor)}>{statusLabel}</span>
              </div>
              {inv.description && <div style={s.invoiceDesc}>{inv.description}</div>}
              <div style={s.invoiceAmount}>${Number(inv.amount).toFixed(2)}</div>
              {isPaid && payment && (
                <div style={s.paidBadge}>
                  💰 Paid by {payment.guestName || payment.buyer?.fullName || "Unknown"}
                  {payment.guestEmail ? ` · ${payment.guestEmail}` : ""}
                </div>
              )}
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 8, marginBottom: 12 }}>
                Created {new Date(inv.createdAt).toLocaleDateString()} · itrustsecure.com/invoice/{inv.slug.slice(0, 8)}...
              </div>
              <div style={s.actionRow}>
                {!isCanceled && (
                  <button style={s.copyBtn} onClick={() => copyLink(inv.slug)}>
                    {copiedId === inv.slug ? "✅ Copied!" : "📋 Copy Payment Link"}
                  </button>
                )}
                {isPaid && payment?.order && (
                  <button style={s.viewBtn} onClick={() => navigate("/orders")}>📦 View Order</button>
                )}
                {!isPaid && !isCanceled && (
                  <button style={s.cancelBtn} onClick={() => handleCancel(inv.id)}>Cancel</button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>🧾 Create Invoice</div>
            <div style={s.modalSub}>Generate a secure payment link to share with your buyer</div>
            <label style={s.label}>What are you selling? *</label>
            <input style={s.input} placeholder="e.g. Custom Logo Design, iPhone 13 Pro" value={title} onChange={e => setTitle(e.target.value)} />
            <label style={s.label}>Description (Optional)</label>
            <textarea style={s.textarea} placeholder="Add details about the item or service..." value={description} onChange={e => setDescription(e.target.value)} />
            <label style={s.label}>Amount ($) *</label>
            <input style={s.input} type="number" step="0.01" min="1" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} />
            {amount && Number(amount) > 0 && (
              <div style={s.summaryBox}>
                <div style={s.summaryRow}><span>Item amount</span><span>${Number(amount).toFixed(2)}</span></div>
                <div style={s.summaryRow}><span>Platform fee (2.5%)</span><span>${platformFee}</span></div>
                <div style={s.summaryTotal}><span>Buyer pays</span><span style={{ color: "#1e40af" }}>${total}</span></div>
              </div>
            )}
            <div style={{ background: "rgba(22,163,74,0.06)", border: "1px solid rgba(22,163,74,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#16a34a" }}>
              🔒 Payment is held in escrow until your buyer confirms receipt
            </div>
            <div style={s.modalActions}>
              <button style={s.modalCancel} onClick={() => setShowCreate(false)}>Cancel</button>
              <button style={{ ...s.modalSubmit, opacity: creating ? 0.5 : 1 }} onClick={handleCreate} disabled={creating}>
                {creating ? "Creating..." : "Create Invoice"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}